import { postBooking } from "../../../app/api/postBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { uuid } from "../../../core/utils/Uuid";
import { isActionRunSuccessfully, shouldSharePlaceWithGuardian } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IApiResponse, IBookedOffer, IBookedTrips, IBookingTypeTrip, IPassenger, IQuickExchangeOfferRequest, OffersRequest, PassengerSpecification, ShuttleOfferRequest } from "../../../Orchestrator/Types/Index";
import { IDefaultActionResult } from "../../../Orchestrator/Types/Interface/ActionParams/DefaultActionResult";
import { IGetAllTripWithoutExchangeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/FilterTripAction";
import { IGetLegsFromTripActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/TrainActions";
import { IBookingRequest, IBookingResponse } from "../../../Orchestrator/Types/Types/BookingTypes";
import { getPriceCategoryOfferPassengerRefs, TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { getLegsSummaryFromTripAction } from "../../UIActions/TrainOffer/GetLegsFromTripAction";
import { fetchExchangeOffersAction } from "../ChangeDate/FetchExchangeOffersAction";
import { startPreExchangeOperationAction } from "../ChangeDate/StartPreExchangeOperationAction";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";
import { getSearchResultAction } from "../Search/GetSearchResultAction";
import dayjs from "dayjs";
import { preferTrainTrips } from "../../../Lib/Utils/booking";

function buildShuttleChangeDatePassengerList(
    passengers: IPassenger[] | undefined,
    selectedPassengerForCancel: string[],
    selectedPassengerForCancelInBound: string[]
) {
    if (!passengers?.length) return [];
    if (passengers.length === 1) {
        return passengers.map((passenger) => ({
            ...passenger,
            detail: {
                ...passenger.detail,
                firstName: passenger.detail?.firstName || "",
                lastName: passenger.detail?.lastName || "",
                email: passenger.detail?.email || "",
            },
        }));
    }
    return passengers
        .filter(
            (p) =>
                selectedPassengerForCancel.includes(p.id) ||
                selectedPassengerForCancelInBound.includes(p.id)
        )
        .map((passenger) => ({
            ...passenger,
            detail: {
                ...passenger.detail,
                firstName: passenger.detail?.firstName || "",
                lastName: passenger.detail?.lastName || "",
                email: passenger.detail?.email || "",
            },
        }));
}

function buildShuttleChangeDateBookedTrip(
    booking: IBookingResponse,
    searchStates: { fromName?: string; toName?: string; from?: string; to?: string } | undefined,
    selectedTrain: TrainType | undefined,
    selectedChangeDateAtom: string,
    selectedPassengers: ReturnType<typeof buildShuttleChangeDatePassengerList>
): IBookedTrips {
    return {
        bookingId: booking?.booking?.id || "",
        fromName: searchStates?.fromName || "",
        toName: searchStates?.toName || "",
        from: searchStates?.from || "",
        to: searchStates?.to || "",
        departureTime: selectedTrain?.departureTime || "",
        arrivalTime: selectedTrain?.arrivalTime || "",
        class: selectedTrain?.selectedPriceClass || "",
        summary:
            selectedTrain?.selectedPriceClass === "premium"
                ? "Premium"
                : "Standard Value",
        type: "Adult",
        passengers: selectedPassengers,
        duration: selectedTrain?.duration || "",
        trainNumber: booking?.booking?.bookingCode || "",
        status: "FULFILLED",
        startDate: selectedChangeDateAtom,
        ticketLink:
            booking?.booking?.fulfillments?.[0]?.fulfillmentDocuments?.[0]
                ?.downloadLink || "",
        arrivalDateTime: selectedTrain?.arrivalTime || "",
        departureDateTime: selectedTrain?.departureTime || "",
        bookingType: "oneWayTrip",
        stops: [],
        inboundData: null,
    };
}

export class GetShuttleOfferBookAction extends BaseAction<{ returnText: string; checkoutRedirect: boolean; }, ShuttleOfferRequest> {

    async processBooking(offerIds: string[] | any, offersRequest: OffersRequest | any, passengerSpecificationsTypes?: any, isFrom?: string, isAnonymous?: boolean | any, callBack?: (status: string) => void) {
        // Prepare the booking parameters        
        const passengerRefs: string[] = [];
        const passengerSpecifications: PassengerSpecification[] = [];

        offersRequest.anonymousPassengerSpecifications.forEach((element: any) => {
            passengerRefs.push(element.externalRef);
            passengerSpecifications.push({
                externalRef: element.externalRef,
                detail: null,
                type: element.type,
                age: element.age,
                cards: (element?.cards || []).length > 0 ? element.cards : [],
                ...(element.prmNeeds?.length > 0 ? { prmNeeds: element.prmNeeds } : {})
            });
        });
        const bookingParams = {
            offers: offerIds.map((offerId: any) => ({
                offerId: offerId,
                passengerRefs: passengerRefs,
            })),
            passengerSpecifications: passengerSpecifications,
            externalRef: uuid(),
        };
        // Construct the IBookingRequest object from the prepared bookingParams
        const newBookingRequest: IBookingRequest = {
            offers: bookingParams.offers,
            passengerSpecifications: bookingParams.passengerSpecifications,
            externalRef: bookingParams.externalRef,
        };

        // Update the booking request state in Recoil
        updateRecoilState<IBookingRequest>('bookingRequestAtom', newBookingRequest);

        try {
            // Clear the booking response atom before making the request
            updateRecoilState('bookingResponseAtom', null);

            // Post booking data to the API
            const response: IApiResponse<IBookingResponse> = await postBooking(newBookingRequest);
            if (response.status === 'ok' && response.answer) {

                const bookingResponse: IBookingResponse = response.answer;
                // Update Recoil state with the booking response                

                updateRecoilState('bookingResponseAtom', bookingResponse);

                prepareBookingSummaryAction.setParams(bookingResponse.booking)
                await prepareBookingSummaryAction.execute()

                if (isAnonymous) {
                    updateRecoilState("guestCheckoutBookingIdAtom", bookingResponse.booking.id)
                }

                // updateRecoilState('isBookReturnJourneyAtom', false);

                // Get Addition Offer Of Selected Offer Like, Meal, Car Parking etc.
                // Start -------------------------------------------------------------                    
                // const additionalOffer: AdditionalOffers = {
                //     outbound: [],
                //     inbound: []
                // }
                // const transformedMealTrain: TransformedMealTrain = {
                //     outbound: [],
                //     inbound: []
                // }
                // const { booking } = bookingResponse

                // const allBookedOfferWithoutExchange: IBookedOffer[] = this.getAllOfferWithoutExchange(booking.bookedOffers)

                // if (isFrom !== "shuttle") {
                //     for (let i = 0; i < allBookedOfferWithoutExchange.length; i++) {
                //         const offerId = allBookedOfferWithoutExchange[i].offerId
                //         const additionOfferResponse: any = await getAdditionalOffers(booking.id, offerId)
                //         if (additionOfferResponse.answer as AdditionalOffersResponse) {
                //             const filteredOffers = (additionOfferResponse?.answer?.offers || []).filter((o: any) =>
                //                 o?.ancillaryOfferParts?.some((part: any) => part?.category === "Meal")
                //             );
                //             if (filteredOffers.length > 0) {
                //                 const firstVegAndNonVegOffer = ["MEAL-VEG", "MEAL-NOVEG"].map(
                //                     (code: string) => filteredOffers.find((o: any) => (o?.products || []).some((p: any) => p.code === code))
                //                 );
                //                 const direction = i === 0 ? "outbound" : "inbound";
                //                 additionalOffer[direction] = firstVegAndNonVegOffer
                //                 transformedMealTrain[direction] = await this.getAllMealOption(firstVegAndNonVegOffer)
                //             }
                //         }
                //     }
                //     updateRecoilState("bookingAdditionOfferAtom", additionalOffer)
                //     updateRecoilState("mealPreferencesResponseAtom", transformedMealTrain)
                //     // End --------------------------------------------------------------       
                // }

                // //Booking Trip Status
                // const tripStatus = this.getAllTripWithoutExchange(allBookedOfferWithoutExchange, booking.trips).length > 1 ? "roundTrip" : "oneWayTrip"

                // //Set Seating Details
                // const passengerSettingDetails: any = []
                // bookingResponse.booking.passengers.forEach(passenger => {
                //     const { id } = passenger
                //     allBookedOfferWithoutExchange[0].reservations.forEach((res) => {
                //         const data = { passengerId: id, coachNumber: "", placeNumbers: "" }
                //         res.placeAllocation.reservedPlaces.forEach(place => {
                //             if (place.passengerIds.includes(id)) {
                //                 data.coachNumber = place.coachNumber
                //                 data.placeNumbers = place.placeNumbers[0]
                //             }
                //         })
                //         passengerSettingDetails.push(data)
                //     })
                // });
                // const uniquesPassengerDetails: SeatingDetails[] = passengerSettingDetails.filter((item: any, index: number, self: any) =>
                //     index === self.findIndex((t: any) => t.passengerId === item.passengerId)
                // );

                // //Set passenger list
                // const passengerList: SelectedPassenger[] = bookingResponse.booking.passengers.map(passenger => {
                //     const passengerObject: SelectedPassenger = {
                //         id: passenger.id,
                //         firstName: passenger?.detail?.firstName || "",
                //         lastName: passenger?.detail?.lastName || "",
                //         email: passenger?.detail?.email || "",
                //         mobile: "",
                //         remember: false,
                //         age: passenger.age,
                //         seatingDetails: uniquesPassengerDetails.find(d => d.passengerId === passenger.id)
                //     }
                //     return passengerObject
                // });

                // if (passengerList.length >= 1) {
                //     updateRecoilState("selectedPassengerForCancel", passengerList.map((d: SelectedPassenger) => d.id))
                //     if (tripStatus === "roundTrip") {
                //         updateRecoilState("selectedPassengerForCancelInBoundAtom", passengerList.map((d: SelectedPassenger) => d.id))
                //     }
                // }
                //await this.calculatePassengerListByPrice(bookingResponse.booking, "", passengerSpecificationsTypes)
                if (callBack) {
                    callBack("success")
                }
            }
        } catch (error: any) {
            console.error('Error processing booking:', error);
        }
    }

    override async run(): Promise<{ returnText: string; checkoutRedirect: boolean; }> {
        let returnText: string = ""
        let checkoutRedirect: boolean = false;
        try {
            const { shuttleTrain, search, isAnonymous, type, totalPassengerCount, passengerCounts, outboundOfferId,
                offersRequest, selectedOutboundTrain, selectedInboundTrain, inboundSearchState, passengerSpecificationsTypes,
                selectedTrainLegs, isShuttleChangeDate, booking, searchStates, selectedTrain, changeTripAlreadyPaidAddons,
                selectedPassengerForCancel, selectedPassengerForCancelInBound, inboundSelectedChangeDateAtom, selectedChangeDateAtom,
                selectedOfferIdChangeDateAtom, currentTrainType
            } = this.params!;
            
            const normalizeIds = (value: any): string[] => {
                if (Array.isArray(value)) {
                    return value.filter((v) => typeof v === "string" && v.trim().length > 0);
                }
                if (typeof value === "string" && value.trim().length > 0) {
                    return [value];
                }
                return [];
            };
            
            const offerId = shuttleTrain.priceInfo.shuttle?.offerId || "";
            const offerIdList = shuttleTrain.priceInfo.shuttle?.offerIds || normalizeIds(offerId);
            const outboundOfferIdList = normalizeIds(outboundOfferId);
            const [startDate, endDate] = [search.outboundDate, search.inboundDate];

            if (isAnonymous) {
                // For outbound journey of a round trip
                if (startDate && endDate && type === TrainTypeEnum.outbound) {
                    // Store outbound selection and continue to inbound selection
                    updateRecoilState("outboundOfferIdAtom", offerId);
                    updateRecoilState("selectedOutboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });

                    const outboundParams: IGetLegsFromTripActionParams = {
                        trip: { legs: shuttleTrain?.legs, priceClass: "" },
                        tripDirection: TrainTypeEnum.outbound
                    }
                    getLegsSummaryFromTripAction.setParams(outboundParams)
                    await getLegsSummaryFromTripAction.execute()

                    updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
                    const searchParams = {
                        fromName: search.toName,
                        toName: search.fromName,
                        departureTime: endDate.format("YYYY-MM-DDTHH:mm:ss"),
                        arrivalTime: startDate.format("YYYY-MM-DDTHH:mm:ss"),
                        origin: search.to,
                        destination: search.from,
                        totalPassengerCount: totalPassengerCount,
                        passengerCounts: passengerCounts,
                        trainType: TrainTypeEnum.inbound,
                    };
                    updateRecoilState("inboundSearchStateAtom", searchParams);
                    //offerService.processSearch(searchParams);
                    getSearchResultAction.setParams(searchParams);
                    getSearchResultAction.execute();
                    returnText = "success";
                } else if (startDate && endDate && type === TrainTypeEnum.inbound) {
                    updateRecoilState("inboundOfferIdAtom", offerId);
                    updateRecoilState("selectedInboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });

                    const inboundParams: IGetLegsFromTripActionParams = {
                        trip: { legs: shuttleTrain?.legs, priceClass: "" },
                        tripDirection: TrainTypeEnum.inbound,
                        trainLegsValue: selectedTrainLegs
                    }
                    getLegsSummaryFromTripAction.setParams(inboundParams)
                    await getLegsSummaryFromTripAction.execute()

                    returnText = "success";
                } else {
                    const selectedDate = search.outboundDate;

                    let offerIds: string[] = [
                        ...offerIdList,
                        ...outboundOfferIdList,
                    ];

                    /**
                     * Commented below code, because no more need to use.
                     * We removed pendingBookingData concept.
                     * Keep code commented out because we need it for shuttle implementation.
                    */

                    // Redirect to login only after both selections are complete                    
                    // sessionStorage.setItem("redirectAfterLogin", "/checkout");
                    // sessionStorage.setItem(
                    //     "pendingBookingData",
                    //     JSON.stringify({
                    //         outboundOfferId:
                    //             type === "outbound" ? offerId : outboundOfferId,
                    //         inboundOfferId: type === "inbound" ? offerId : null,
                    //         offersRequest,
                    //         selectedOutboundTrain,
                    //         selectedInboundTrain,
                    //         search,
                    //         inboundSearchState,
                    //         selectedDate,
                    //         passengers: passengerCounts,
                    //         //Below two is for shuttle
                    //         trainType: "shuttle",
                    //         shuttleTrain: shuttleTrain,
                    //         passengerSpecificationsTypes: passengerSpecificationsTypes,
                    //         offerIds: offerIds
                    //     })
                    // );
                    // returnText = "needLogin";
                    returnText = "success";
                }
            } else {
                returnText = "success"
            }

            //Handling  exchangeOperation and additional offers API call
            // if (isShuttleChangeDate && currentTrainType === "outbound") {
            //     let selectedPassengers;

            //     const offerIdsChangeDate: any =
            //         startDate && endDate
            //             ? [offerId, SelectedOfferIdChangeDateAtom]
            //             : [offerId];

            //     // If booking has only one passenger, use it directly
            //     if (booking?.booking?.passengers?.length === 1) {
            //         selectedPassengers = booking.booking.passengers.map((passenger) => ({
            //             ...passenger,
            //             detail: {
            //                 ...passenger.detail,
            //                 firstName: passenger.detail?.firstName || "",
            //                 lastName: passenger.detail?.lastName || "",
            //                 email: passenger.detail?.email || "",
            //             },
            //         }));
            //     } else {
            //         selectedPassengers =
            //             booking?.booking?.passengers
            //                 .filter(
            //                     (p) =>
            //                         selectedPassengerForCancel.includes(p.id) ||
            //                         selectedPassengerForCancelInBound.includes(p.id)
            //                 )
            //                 .map((passenger) => ({
            //                     ...passenger,
            //                     detail: {
            //                         ...passenger.detail,
            //                         firstName: passenger.detail?.firstName || "",
            //                         lastName: passenger.detail?.lastName || "",
            //                         email: passenger.detail?.email || "",
            //                     },
            //                 })) || [];
            //     }

            //     const bookedTrip: BookedTrips = {
            //         bookingId: booking?.booking?.id || "",
            //         fromName: searchStates.fromName,
            //         toName: searchStates.toName,
            //         from: searchStates.from,
            //         to: searchStates.to,
            //         departureTime: selectedTrain?.departureTime || "",
            //         arrivalTime: selectedTrain?.arrivalTime || "",
            //         class: "",
            //         summary: "",
            //         type: "Adult",
            //         passengers: selectedPassengers,
            //         duration: selectedTrain?.duration || "",
            //         trainNumber: booking?.booking?.bookingCode || "",
            //         status: "FULFILLED",
            //         startDate: selectedDate,
            //         ticketLink:
            //             booking?.booking?.fulfillments?.[0]?.fulfillmentDocuments?.[0]
            //                 ?.downloadLink || "",
            //         arrivalDateTime: selectedTrain?.arrivalTime || "",
            //         departureDateTime: selectedTrain?.departureTime || "",
            //         bookingType: "oneWayTrip",
            //         stops: [],
            //         inboundData: null,
            //     };

            //     await bookingService.preExchangeOperationStartChangeDate(offerIdsChangeDate, bookedTrip);
            //     routeTo("/checkout");
            // }

            // Handle logged-in user flow
            if (returnText === "success") {
                if (isShuttleChangeDate) {

                    if (booking && (selectedPassengerForCancel || selectedPassengerForCancelInBound)) {
                        if (inboundSelectedChangeDateAtom && currentTrainType === TrainTypeEnum.outbound) {
                            updateRecoilState("selectedOutboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });
                            updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);

                            const anonymousPassengerSpecifications =
                                booking?.booking?.passengers
                                    .filter(
                                        (p) =>
                                            selectedPassengerForCancel!.includes(p.id) ||
                                            selectedPassengerForCancelInBound!.includes(p.id)
                                    )
                                    .map((passenger) => ({
                                        id: passenger.id,
                                        type: passenger.type,
                                        externalRef: passenger.externalRef,
                                        age: passenger.age,
                                        cards: passenger.cards || [],
                                        prmNeeds: passenger.prmNeeds || [],
                                        sharePlaceWithGuardian: shouldSharePlaceWithGuardian(passenger) ? true : undefined
                                    })) || [];


                            await getAllOfferWithoutExchangeAction.setParams(booking?.booking?.bookedOffers)
                            await getAllOfferWithoutExchangeAction.execute()
                            let bookingOfferWithoutExchange: IBookedOffer[] = getAllOfferWithoutExchangeAction.result!


                            const params: IGetAllTripWithoutExchangeActionParams = {
                                bookedOffers: bookingOfferWithoutExchange,
                                trips: booking?.booking?.trips
                            }
                            await getAllTripWithoutExchangeAction.setParams(params)
                            await getAllTripWithoutExchangeAction.execute()
                            const allTripWithoutExchange: IBookingTypeTrip[] = getAllTripWithoutExchangeAction.result!


                            const inboundBookedOfferOnly = bookingOfferWithoutExchange.filter(offer => preferTrainTrips(allTripWithoutExchange).find(trip => trip.direction === TripTypeEnum.IN_BOUND)?.id === offer?.tripCoverage?.coveredTripId)
                            // ERM infant–adult linking: the backend requires the WHOLE
                            // linked group in the exchange request (400 PASSENGERS_MISSING_CHILDREN
                            // otherwise), so lap-infant admissions are included with their adult.
                            let admissions = inboundBookedOfferOnly?.flatMap(
                                (offer) =>
                                    offer?.admissions?.filter((admission) =>
                                        admission.status === "FULFILLED" &&
                                        admission.passengerIds.some((pid) =>
                                            anonymousPassengerSpecifications.some((p) => p.id === pid)
                                        )
                                    ) || []
                            );

                            const fulfillmentIds: string[] = [];

                            admissions?.forEach((element) => {
                                fulfillmentIds.push(element.id);
                            });

                            // Calculate original price of items being exchanged
                            let originalPriceOfExchangedItemsOutbound = 0;
                            if (booking?.booking?.bookedOffers && fulfillmentIds.length > 0) {
                                booking.booking.bookedOffers.forEach(offer => {
                                    offer.admissions?.forEach(admission => {
                                        if (fulfillmentIds.includes(admission.id)) {
                                            originalPriceOfExchangedItemsOutbound += admission.price.amount;
                                        }
                                    });
                                });
                            }

                            if (originalPriceOfExchangedItemsOutbound > 0) {
                                const scale = booking.booking.bookedOffers[0]?.admissions[0]?.price?.scale;
                                updateRecoilState("originalPriceOfExchangedItemsInboundAtom", null);
                                updateRecoilState("originalPriceOfExchangedItemsInboundAtom", (originalPriceOfExchangedItemsOutbound / ((100 * scale) / scale)));
                            }

                            const fallbackInboundTrip = preferTrainTrips(booking?.booking?.trips).find(t => t.direction === TripTypeEnum.IN_BOUND);
                            const exchangeOffersRequest: IQuickExchangeOfferRequest = {
                                fulfillmentIds,
                                overruleCode: "",
                                tripSearchCriteria: {
                                    departureTime: inboundSelectedChangeDateAtom
                                        ? dayjs(new Date(inboundSelectedChangeDateAtom)).format("YYYY-MM-DD") + "T00:00:00" : "",
                                    origin: {
                                        objectType: "StopPlaceRef",
                                        stopPlaceRef: fallbackInboundTrip?.origin.stopPlaceRef || "",
                                    },
                                    destination: {
                                        objectType: "StopPlaceRef",
                                        stopPlaceRef: fallbackInboundTrip?.destination.stopPlaceRef || "",
                                    },
                                    direction: TripTypeEnum.IN_BOUND,
                                    // returnSearchParameters: (inboundSelectedChangeDateAtom && selectedChangeDateAtom) ?
                                    //     { outwardTripDate: dayjs(selectedChangeDateAtom).format("YYYY-MM-DD[T]00:00:00.000[Z]") } : null
                                },
                                offerSearchCriteria: {
                                    currency: "AED",
                                },
                                // Include the full linked group; sharePlaceWithGuardian marks lap infants.
                                anonymousPassengerSpecifications:
                                    anonymousPassengerSpecifications
                                        .map((passenger) => {
                                            return {
                                                externalRef: passenger.externalRef,
                                                type: passenger.type,
                                                cards: passenger?.cards || [],
                                                prmNeeds: passenger.prmNeeds || [],
                                                age: passenger?.age,
                                                sharePlaceWithGuardian: passenger.sharePlaceWithGuardian || undefined,
                                            }
                                        }),
                            };

                            try {
                                await fetchExchangeOffersAction.setParams(
                                    {
                                        booking_id: booking?.booking.id,
                                        exchangeOffersRequest: exchangeOffersRequest,
                                        booking: booking,
                                        originalPriceOfExchangedItems: originalPriceOfExchangedItemsOutbound,
                                        trainType: TrainTypeEnum.inbound,
                                        selectedDate: selectedChangeDateAtom!,
                                        inboundSelectedDate: inboundSelectedChangeDateAtom!
                                    }
                                )
                                await fetchExchangeOffersAction.execute();

                                const outboundParams: IGetLegsFromTripActionParams = {
                                    trip: { legs: shuttleTrain?.legs, priceClass: "" },
                                    tripDirection: TrainTypeEnum.outbound
                                }
                                getLegsSummaryFromTripAction.setParams(outboundParams)
                                await getLegsSummaryFromTripAction.execute()

                                updateRecoilState("SelectedOfferIdChangeDateAtom", offerId);
                            } catch (error) {
                                console.error("Error fetching exchange offers:", error);
                            }
                        }
                        else if (inboundSelectedChangeDateAtom && currentTrainType === TrainTypeEnum.inbound) {
                            const offerIdsChangeDate = [offerId];
                            if (inboundSelectedChangeDateAtom && selectedOfferIdChangeDateAtom) {
                                offerIdsChangeDate.push(selectedOfferIdChangeDateAtom);
                            }

                            const selectedPassengers = buildShuttleChangeDatePassengerList(
                                booking?.booking?.passengers,
                                selectedPassengerForCancel!,
                                selectedPassengerForCancelInBound!
                            );
                            const bookedTrip = buildShuttleChangeDateBookedTrip(
                                booking!,
                                searchStates,
                                selectedTrain,
                                selectedChangeDateAtom!,
                                selectedPassengers
                            );

                            const inboundOfferPassengerRefs = getPriceCategoryOfferPassengerRefs(
                                shuttleTrain.priceInfo,
                                shuttleTrain.selectedPriceClass
                            );
                            const outboundOfferPassengerRefs = getPriceCategoryOfferPassengerRefs(
                                selectedTrain?.priceInfo,
                                selectedTrain?.selectedPriceClass
                            );
                            const combinedOfferPassengerRefs = [...inboundOfferPassengerRefs, ...outboundOfferPassengerRefs];

                            //pre exchange operation
                            await startPreExchangeOperationAction.setParams({
                                exchangeOfferId: offerIdsChangeDate?.filter((id): id is string => id !== undefined) ?? [],
                                exchangeOfferPassengerRefs: combinedOfferPassengerRefs.length > 0 ? combinedOfferPassengerRefs : undefined,
                                nextTrainTicket: bookedTrip,
                                selectedPassengerForCancel: selectedPassengerForCancel!,
                                selectedPassengerForCancelInBound: selectedPassengerForCancelInBound!,
                                changeTripAlreadyPaidAddons: changeTripAlreadyPaidAddons
                            });
                            await startPreExchangeOperationAction.execute();

                            if (isActionRunSuccessfully(startPreExchangeOperationAction)) {
                                const fetchResult: IDefaultActionResult = startPreExchangeOperationAction.result!
                                if (fetchResult.status == "success") {
                                    updateRecoilState("selectedInboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });

                                    const inboundParams: IGetLegsFromTripActionParams = {
                                        trip: { legs: shuttleTrain?.legs, priceClass: "" },
                                        tripDirection: TrainTypeEnum.inbound,
                                        trainLegsValue: selectedTrainLegs
                                    }
                                    getLegsSummaryFromTripAction.setParams(inboundParams)
                                    await getLegsSummaryFromTripAction.execute()
                                    checkoutRedirect = true;
                                }
                            }
                        }
                        else {
                            const offerIdsChangeDate = [offerId];
                            if (inboundSelectedChangeDateAtom && selectedOfferIdChangeDateAtom) {
                                offerIdsChangeDate.push(selectedOfferIdChangeDateAtom);
                            }

                            const selectedPassengers = buildShuttleChangeDatePassengerList(
                                booking?.booking?.passengers,
                                selectedPassengerForCancel!,
                                selectedPassengerForCancelInBound!
                            );
                            const bookedTrip = buildShuttleChangeDateBookedTrip(
                                booking!,
                                searchStates,
                                selectedTrain,
                                selectedChangeDateAtom!,
                                selectedPassengers
                            );

                            const currentOfferPassengerRefs = getPriceCategoryOfferPassengerRefs(
                                shuttleTrain.priceInfo,
                                shuttleTrain.selectedPriceClass
                            );

                            //pre exchange operation
                            await startPreExchangeOperationAction.setParams({
                                exchangeOfferId: offerIdsChangeDate?.filter((id): id is string => id !== undefined) ?? [],
                                exchangeOfferPassengerRefs: currentOfferPassengerRefs.length > 0 ? currentOfferPassengerRefs : undefined,
                                nextTrainTicket: bookedTrip,
                                selectedPassengerForCancel: selectedPassengerForCancel!,
                                selectedPassengerForCancelInBound: selectedPassengerForCancelInBound!,
                                changeTripAlreadyPaidAddons: changeTripAlreadyPaidAddons
                            });
                            await startPreExchangeOperationAction.execute();

                            if (isActionRunSuccessfully(startPreExchangeOperationAction)) {
                                const fetchResult: IDefaultActionResult = startPreExchangeOperationAction.result!
                                if (fetchResult.status == "success") {
                                    updateRecoilState("selectedOutboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });
                                    const params: IGetLegsFromTripActionParams = {
                                        trip: { legs: shuttleTrain?.legs, priceClass: "" },
                                        tripDirection: TrainTypeEnum.outbound
                                    }
                                    getLegsSummaryFromTripAction.setParams(params)
                                    await getLegsSummaryFromTripAction.execute()
                                    checkoutRedirect = true;
                                }
                            }
                        }
                    }
                }
                else {
                    //Normal Booking Flow
                    if (startDate && endDate && type === TrainTypeEnum.outbound) {
                        updateRecoilState("outboundOfferIdAtom", offerId);
                        updateRecoilState("selectedOutboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });

                        const outboundParams: IGetLegsFromTripActionParams = {
                            trip: { legs: shuttleTrain?.legs, priceClass: "" },
                            tripDirection: TrainTypeEnum.outbound
                        }
                        getLegsSummaryFromTripAction.setParams(outboundParams)
                        await getLegsSummaryFromTripAction.execute()

                        updateRecoilState("trainTypeAtom", TrainTypeEnum.inbound);
                        const searchParams = {
                            fromName: search.toName,
                            toName: search.fromName,
                            departureTime: endDate.format("YYYY-MM-DDTHH:mm:ss"),
                            arrivalTime: startDate.format("YYYY-MM-DDTHH:mm:ss"),
                            origin: search.to,
                            destination: search.from,
                            totalPassengerCount: totalPassengerCount,
                            passengerCounts: passengerCounts,
                            trainType: TrainTypeEnum.inbound,
                            selectedTrain: selectedOutboundTrain
                        };
                        updateRecoilState("inboundSearchStateAtom", searchParams);
                        //offerService.processSearch(searchParams);
                        getSearchResultAction.setParams(searchParams);
                        getSearchResultAction.execute();
                    } else if (startDate && endDate && type === TrainTypeEnum.inbound) {
                        updateRecoilState("inboundOfferIdAtom", offerId);
                        updateRecoilState("selectedInboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });

                        const inboundParams: IGetLegsFromTripActionParams = {
                            trip: { legs: shuttleTrain?.legs, priceClass: "" },
                            tripDirection: TrainTypeEnum.inbound,
                            trainLegsValue: selectedTrainLegs
                        }
                        getLegsSummaryFromTripAction.setParams(inboundParams)
                        await getLegsSummaryFromTripAction.execute()

                        let offerIds: string[] = [
                            ...offerIdList,
                            ...outboundOfferIdList,
                        ];
                        await this.processBooking(
                            offerIds,
                            offersRequest,
                            passengerSpecificationsTypes,
                            "shuttle",
                            isAnonymous,
                            (status: string) => {
                                if (status === "success") {
                                }
                            }
                        );
                        checkoutRedirect = true;
                    } else {
                        updateRecoilState("selectedOutboundTrainAtom", { ...shuttleTrain, trainType: "shuttle" });
                        // updateRecoilState("isShuttleBookingAtom", true);

                        /**
                         * Set legs summary for shuttle train.
                         * This is used to show the selected train details in the summary page. 
                        */
                        const params: IGetLegsFromTripActionParams = {
                            trip: { legs: shuttleTrain?.legs, priceClass: "" },
                            tripDirection: TrainTypeEnum.outbound
                        }
                        getLegsSummaryFromTripAction.setParams(params)
                        await getLegsSummaryFromTripAction.execute()

                        let offerIds: string[] = [
                            ...offerIdList,
                            ...outboundOfferIdList,
                        ];
                        await this.processBooking(
                            offerIds,
                            offersRequest,
                            passengerSpecificationsTypes,
                            "shuttle",
                            isAnonymous,
                            (status: string) => {
                                if (status === "success") {

                                    //routeTo("/checkout");
                                }
                            }
                        );
                        checkoutRedirect = true;
                    }
                }
            }
        } catch (error) {
            console.error("Error during sign in:", error);
        } finally {
            return { returnText, checkoutRedirect }
        }
    }
}

// Create a singleton instance of the action
export const getShuttleOfferBookAction = new GetShuttleOfferBookAction();
